import React, { useCallback } from 'react';
import { requestHandler } from '../utils';
import SocketContext from './socketContext';
import UserContext from './userContext';

const initCountersValue = {
    unreadDirectMessages: 0,
    unreadContentApprovalMessages: 0,
    total: 0,
};
const UnreadMessagesContext = React.createContext({
    unreadMessageCounters: initCountersValue,
    fetchUnreadMessageCounters: () => {
        // Do nothing
    },
});

export const UnreadMessagesProvider = ({ children }: { children: React.ReactNode }) => {
    const [unreadMessageCounters, setUnreadMessageCounters] = React.useState(initCountersValue);
    const user = React.useContext(UserContext);
    const socket = React.useContext(SocketContext);

    const fetchUnreadMessageCounters = useCallback(async () => {
        if (user.activeClient) {
            const res = await requestHandler(
                'GET',
                `/api/unreadMessagesCounters/${Number(user.activeClient.id)}`,
            );
            // biome-ignore lint/suspicious/noExplicitAny: legacy code
            if (res.success && (res.payload as any)?.length > 0) {
                // biome-ignore lint/suspicious/noExplicitAny: legacy code
                const counters = res.payload as any;
                const countersObject = counters[0];
                countersObject.total = Number(
                    countersObject.unreadDirectMessages +
                        countersObject.unreadContentApprovalMessages,
                );
                setUnreadMessageCounters(countersObject);
            }
        }
    }, [user]);

    // JOIN MESSAGIN ROOMS
    React.useEffect(() => {
        if (socket && user?.activeClient?.accountName) {
            socket.emit('join room', `${user.activeClient.accountName}-dm`);
            socket.emit('join room', `${user.activeClient.accountName}-community-chat`);
            socket.emit('join room', `${user.activeClient.accountName}-content-approval-msg`);
        }

        return () => {
            if (socket && user?.activeClient?.accountName) {
                socket.emit('leave room', `${user.activeClient.accountName}-dm`);
                socket.emit('leave room', `${user.activeClient.accountName}-community-chat`);
                socket.emit('leave room', `${user.activeClient.accountName}-content-approval-msg`);
            }
        };
    }, [socket, user]);

    // SUBSCRIBE TO CLIENT/BRAND MESSAGES EVENTS
    React.useEffect(() => {
        const onMessage = async () => {
            await fetchUnreadMessageCounters();
        };

        if (socket) {
            socket.on('new brand dm', onMessage);
            socket.on('new community msg', onMessage);
            socket.on('new brand content-approval', onMessage);
        }

        return () => {
            if (socket) {
                socket.removeListener('new brand dm', onMessage);
                socket.removeListener('new community msg', onMessage);
                socket.removeListener('new brand content-approval', onMessage);
            }
        };
    }, [fetchUnreadMessageCounters, socket]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    React.useEffect(() => {
        fetchUnreadMessageCounters();
    }, [fetchUnreadMessageCounters, user]);

    return (
        <UnreadMessagesContext.Provider
            value={{ unreadMessageCounters, fetchUnreadMessageCounters }}
        >
            {children}
        </UnreadMessagesContext.Provider>
    );
};

export default UnreadMessagesContext;
